// ── Pagination page-window (pure) ────────────────────────────────────────────
//
// Builds the numbered-pager token list — first/last always shown, a sibling
// window around the current page, and "…" for collapsed gaps. Pure + deterministic
// so it can be unit-tested without a render harness. All page numbers are
// 1-indexed for display.

export type PageToken = number | "ellipsis";

/**
 * Tokens to render for a numbered pager.
 *
 * @param current      1-indexed current page
 * @param totalPages   total number of pages (>= 1)
 * @param siblings     pages to show on EACH side of current (default 2)
 * @param boundary     pages to always show at each end (default 1)
 *
 * Guarantees: never emits an "ellipsis" that hides a single page (shows the page
 * instead), never emits a page < 1 or > totalPages, no duplicates, strictly
 * ascending page numbers.
 */
export function pageWindow(
  current: number,
  totalPages: number,
  siblings = 2,
  boundary = 1,
): PageToken[] {
  const total = Math.max(1, Math.floor(totalPages));
  const cur = Math.min(total, Math.max(1, Math.floor(current)));

  // Small enough to show every page — no ellipsis needed.
  // Threshold = boundaries on both ends + the sibling window + the 2 ellipsis slots.
  const full = boundary * 2 + siblings * 2 + 3;
  if (total <= full) return range(1, total);

  const left = Math.max(cur - siblings, boundary + 1);
  const right = Math.min(cur + siblings, total - boundary);

  const tokens: PageToken[] = [];
  tokens.push(...range(1, boundary));

  // Left gap: a single hidden page (== boundary+2 exposed) is shown, not an ellipsis.
  if (left > boundary + 2) tokens.push("ellipsis");
  else if (left === boundary + 2) tokens.push(boundary + 1);

  tokens.push(...range(left, right));

  if (right < total - boundary - 1) tokens.push("ellipsis");
  else if (right === total - boundary - 1) tokens.push(total - boundary);

  tokens.push(...range(total - boundary + 1, total));
  return tokens;
}

/** Total page count for a result total + page size (always >= 1). */
export function pageCount(total: number, pageSize: number): number {
  const t = Math.max(0, Math.floor(total));
  const s = Math.max(1, Math.floor(pageSize));
  return Math.max(1, Math.ceil(t / s));
}

/** Clamp a 0-indexed page into [0, totalPages-1]. */
export function clampPage(page: number, totalPages: number): number {
  const last = Math.max(1, Math.floor(totalPages)) - 1;
  return Math.min(last, Math.max(0, Math.floor(page)));
}

function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i += 1) out.push(i);
  return out;
}
